
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FAQSyncData {
  faq_id: string;
  question: string;
  answer: string;
  association_id: number;
  action: 'update' | 'delete'; // Removed 'create' action
}

export const useFAQSync = () => {
  const checkFrankIntegrationEnabled = async (organizationId: string): Promise<{enabled: boolean, apiUrl?: string}> => {
    try {
      const { data, error } = await supabase
        .from('frank_api_config')
        .select('api_url, is_enabled')
        .eq('organization_id', organizationId)
        .single();

      if (error || !data) {
        console.log('No Frank API configuration found, sync disabled');
        return { enabled: false };
      }

      return { 
        enabled: data.is_enabled, 
        apiUrl: data.api_url 
      };
    } catch (error) {
      console.error('Error checking Frank integration status:', error);
      return { enabled: false };
    }
  };

  const syncFAQToFrank = async (data: FAQSyncData, organizationId?: string) => {
    try {
      console.log('Checking Frank integration status before sync...');
      
      if (!organizationId) {
        console.log('No organization ID provided, skipping Frank sync');
        return { success: true, message: 'No organization context for sync' };
      }

      const { enabled, apiUrl } = await checkFrankIntegrationEnabled(organizationId);
      
      if (!enabled) {
        console.log('Frank integration is disabled, skipping sync');
        // Update FAQ item to mark sync as not attempted
        await supabase
          .from('faq_items')
          .update({
            sent_to_frank: false,
            last_sync_attempt: new Date().toISOString(),
            sync_error_message: 'Frank integration is disabled'
          })
          .eq('id', data.faq_id);

        return { success: true, message: 'Frank integration is disabled, FAQ saved locally only' };
      }

      if (!apiUrl) {
        console.log('No Frank API URL configured, skipping sync');
        await supabase
          .from('faq_items')
          .update({
            sent_to_frank: false,
            last_sync_attempt: new Date().toISOString(),
            sync_error_message: 'No Frank API URL configured'
          })
          .eq('id', data.faq_id);

        return { success: true, message: 'No Frank API URL configured, FAQ saved locally only' };
      }

      console.log('Frank integration is enabled, attempting sync to:', apiUrl);
      
      // Update FAQ item to show sync is being attempted
      await supabase
        .from('faq_items')
        .update({
          last_sync_attempt: new Date().toISOString(),
          sync_error_message: null
        })
        .eq('id', data.faq_id);

      const { data: response, error } = await supabase.functions.invoke('sync-faq-to-frank', {
        body: { ...data, organization_id: organizationId }
      });

      if (error) {
        console.error('Error invoking sync function:', error);
        throw error;
      }

      if (response?.success) {
        console.log('FAQ successfully synced to Frank');
        toast.success('FAQ synced to Frank successfully');
        return { success: true };
      } else {
        console.error('Frank sync failed:', response);
        const errorMessage = response?.error || 'Unknown error';
        
        // Update FAQ item with sync error
        await supabase
          .from('faq_items')
          .update({
            sent_to_frank: false,
            sync_error_message: errorMessage
          })
          .eq('id', data.faq_id);

        toast.error(`Failed to sync FAQ to Frank: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      console.error('Error syncing FAQ to Frank:', error);
      
      // Update FAQ item with sync error
      if (data.faq_id) {
        await supabase
          .from('faq_items')
          .update({
            sent_to_frank: false,
            sync_error_message: error.message || 'Sync failed'
          })
          .eq('id', data.faq_id);
      }

      toast.error('Failed to sync FAQ to Frank - check Frank API configuration');
      return { success: false, error: error.message };
    }
  };

  const getAssociationId = async (organizationId: string): Promise<number | null> => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('association_id')
        .eq('id', organizationId)
        .single();

      if (error || !data) {
        console.error('Error fetching association ID:', error);
        return null;
      }

      return data.association_id;
    } catch (error) {
      console.error('Error getting association ID:', error);
      return null;
    }
  };

  return {
    syncFAQToFrank,
    getAssociationId,
    checkFrankIntegrationEnabled
  };
};
